import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Popover } from '@/components/ui/popover';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ReviewStatusChip } from '@/components/review-status-chip';
import { useCurrentIdentity } from '@/lib/identity-context';
import { formatDateTime } from '@/lib/format';

type ReviewStatus = components['schemas']['ReviewStatus'];
type ReviewEntityType = components['schemas']['ReviewEntityType'];
type FindingReview = components['schemas']['FindingReview'];

interface ReviewPopoverProps {
  crawlId: string;
  entityType: ReviewEntityType;
  entityId: string;
  currentStatus: ReviewStatus;
  currentNote?: string | null;
}

// Per-finding review workflow UI. Shared by Scripts, Cookies, Payloads,
// GTM, and Findings tabs. The audit chain is fetched lazily when the
// popover opens (via the `enabled: open` flag) so list rows don't
// pre-load N audit histories on render.
export function ReviewPopover({
  crawlId,
  entityType,
  entityId,
  currentStatus,
  currentNote,
}: ReviewPopoverProps) {
  const { hasPermission } = useCurrentIdentity();
  const canWrite = hasPermission('findings:review');
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ReviewStatus>(currentStatus);
  const [note, setNote] = useState(currentNote ?? '');
  const queryClient = useQueryClient();

  const historyQuery = useQuery({
    queryKey: queryKeys.reviews.history(crawlId, entityType, entityId),
    enabled: open,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/reviews/{crawlId}/{entityType}/{entityId}',
        { params: { path: { crawlId, entityType, entityId } } },
      );
      if (error) throw error;
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (): Promise<FindingReview> => {
      const { data, error } = await apiClient.POST('/reviews', {
        body: { crawlId, entityType, entityId, status, note },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews.history(crawlId, entityType, entityId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crawls.reviews(crawlId) });
    },
  });

  return (
    <Popover
      align="end"
      trigger={<ReviewStatusChip status={currentStatus} onClick={() => setOpen(true)} />}
    >
      {(close) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Review</span>
            <ReviewStatusChip status={status} />
          </div>
          <label className="flex flex-col gap-1 text-xs font-medium">
            Status
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as ReviewStatus)}
              disabled={!canWrite}
              data-testid="review-status-select"
            >
              <option value="unreviewed">Unreviewed</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="approved">Approved</option>
              <option value="flagged">Flagged</option>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium">
            Note
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Context for this review (reviewers see this in the audit chain)."
              disabled={!canWrite}
              data-testid="review-note-input"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canWrite || mutation.isPending}
              onClick={() => {
                mutation.mutate(undefined, { onSuccess: () => close() });
              }}
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {historyQuery.data && historyQuery.data.history.length > 0 && (
            <div className="border-t pt-2">
              <div className="mb-1 text-xs font-semibold text-muted-foreground">Audit chain</div>
              <ul className="flex flex-col gap-1 text-xs">
                {historyQuery.data.history.map((h: FindingReview) => (
                  <li key={h.id} className="flex items-center justify-between gap-2">
                    <ReviewStatusChip status={h.status} />
                    <span className="truncate text-muted-foreground">
                      {h.reviewedBy?.email ?? 'system'} · {formatDateTime(h.reviewedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}
